import { Link, useLocation } from 'react-router-dom';

export interface BottomNavItem {
  to?: string;
  label: string;
  icon: string;
  onClick?: () => void;
  exact?: boolean;
}

export interface BottomNavMoreButton {
  label: string;
  icon: string;
  onClick: () => void;
  active?: boolean;
}

interface BottomNavProps {
  items: BottomNavItem[];
  height?: number;
  activeClass?: string;
  inactiveClass?: string;
  moreButton?: BottomNavMoreButton;
  bgImage?: string | null;
  accentColor?: string;
}

export default function BottomNav({
  items,
  height = 64,
  activeClass = 'text-teal-600',
  inactiveClass = 'text-gray-400',
  moreButton,
  bgImage,
  accentColor,
}: BottomNavProps) {
  const location = useLocation();

  const isActive = (item: BottomNavItem) => {
    if (!item.to) return false;
    if (item.exact) return location.pathname === item.to;
    return (
      location.pathname === item.to ||
      location.pathname.startsWith(item.to + '/')
    );
  };

  const resolvedActiveClass = bgImage ? 'text-white' : activeClass;
  const resolvedInactiveClass = bgImage ? 'text-white/50' : inactiveClass;
  const activeDotClass = bgImage ? 'bg-white' : 'bg-teal-600';

  const accentStyle = accentColor && !bgImage ? { color: accentColor } : undefined;

  return (
    <nav
      className={`flex-shrink-0 border-t flex items-center justify-around z-50 ${bgImage ? 'bg-black/40 backdrop-blur-md border-t-white/20' : 'bg-white border-t-gray-100'}`}
      style={{ height: `${height}px`, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {items.map((item) => {
        const active = isActive(item);
        if (item.onClick) {
          return (
            <button
              key={item.label}
              onClick={item.onClick}
              className={`relative flex flex-col items-center justify-center gap-1 flex-1 h-full cursor-pointer select-none min-h-[44px] pt-1 ${active ? resolvedActiveClass : resolvedInactiveClass}`}
              aria-label={item.label}
              style={active && accentStyle ? accentStyle : undefined}
            >
              <div className="w-7 h-7 flex items-center justify-center">
                <i className={`${item.icon} text-xl`}></i>
              </div>
              <span className="text-[12px] font-medium leading-tight whitespace-nowrap">
                {item.label}
              </span>
              {active && (
                <div className={`absolute bottom-[calc(0.25rem+env(safe-area-inset-bottom,0px))] w-5 h-1 ${activeDotClass} rounded-full`} style={accentStyle ? { backgroundColor: accentColor } : undefined}></div>
              )}
            </button>
          );
        }
        return (
          <Link
            key={item.to}
            to={item.to!}
            className={`relative flex flex-col items-center justify-center gap-1 flex-1 h-full cursor-pointer select-none transition-colors min-h-[44px] pt-1 ${active ? resolvedActiveClass : resolvedInactiveClass}`}
            aria-label={item.label}
            style={active && accentStyle ? accentStyle : undefined}
          >
            <div className={`w-7 h-7 flex items-center justify-center ${active ? resolvedActiveClass : ''}`}>
              <i className={`${item.icon} text-xl`}></i>
            </div>
            <span className="text-[12px] font-medium leading-tight whitespace-nowrap">
              {item.label}
            </span>
            {active && (
              <div className={`absolute bottom-[calc(0.25rem+env(safe-area-inset-bottom,0px))] w-5 h-1 ${activeDotClass} rounded-full`} style={accentStyle ? { backgroundColor: accentColor } : undefined}></div>
            )}
          </Link>
        );
      })}
      {moreButton && (
        <button
          onClick={moreButton.onClick}
          className={`relative flex flex-col items-center justify-center gap-1 flex-1 h-full cursor-pointer select-none min-h-[44px] pt-1 ${moreButton.active ? resolvedActiveClass : resolvedInactiveClass}`}
          aria-label={moreButton.label}
        >
          <div className="w-7 h-7 flex items-center justify-center">
            <i className={`${moreButton.icon} text-xl`}></i>
          </div>
          <span className="text-[12px] font-medium leading-tight whitespace-nowrap">
            {moreButton.label}
          </span>
        </button>
      )}
    </nav>
  );
}